import React, { useEffect, useState, useMemo } from 'react'
import { Loader2 } from 'lucide-react'
import { useTranslation } from '../hooks/use-i18n'
import formula1Service from '../services/formula1Service'
import { Formula1Race, Formula1Prediction } from '../types/formula1'
import Formula1PredictedGrid from './Formula1PredictedGrid'
import Formula1PlacePredictionsModal, { PlacePredictionRow } from './Formula1PlacePredictionsModal'

interface Formula1RacePredictionsModalProps {
  roomId: string
  race: Formula1Race | null
  isOpen: boolean
  onClose: () => void
}

type PredictionWithPlayer = Formula1Prediction & { player?: { name: string } }

const Formula1RacePredictionsModal: React.FC<Formula1RacePredictionsModalProps> = ({
  roomId,
  race: initialRace,
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation()
  const [race, setRace] = useState<Formula1Race | null>(initialRace)
  const [predictions, setPredictions] = useState<PredictionWithPlayer[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedPlace, setSelectedPlace] = useState<number | null>(null)

  useEffect(() => {
    if (!isOpen || !roomId || !initialRace) {
      setRace(null)
      setPredictions([])
      setSelectedPlace(null)
      return
    }
    const raceId = initialRace.id ?? initialRace._id
    if (!raceId) return
    let cancelled = false
    setLoading(true)
    setSelectedPlace(null)
    Promise.all([
      formula1Service.getRace(raceId),
      formula1Service.getRoomPredictions(roomId),
    ])
      .then(([raceData, roomPreds]) => {
        if (cancelled) return
        setRace(raceData)
        const list = Array.isArray(roomPreds) ? roomPreds : []
        const forThisRace = list.filter((p: Formula1Prediction) => {
          const pRaceId = p.raceId ?? (p.race as any)?.id ?? (p.race as any)?._id
          return pRaceId === raceId
        })
        setPredictions(forThisRace as PredictionWithPlayer[])
      })
      .catch(() => {
        if (!cancelled) setPredictions([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [isOpen, roomId, initialRace])

  const actualResultsList = useMemo(() => {
    if (!race?.actualResults || typeof race.actualResults !== 'object') return []
    return Object.entries(race.actualResults)
      .sort(([, a], [, b]) => a - b)
      .slice(0, 10)
      .map(([driver, position]) => ({ position, driver }))
  }, [race?.actualResults])

  const predictionsByPlace = useMemo(() => {
    const map: Record<number, PlacePredictionRow[]> = {}
    for (let pos = 1; pos <= 10; pos++) {
      map[pos] = predictions
        .map((p) => {
          const driver = Object.entries(p.predictedPositions || {}).find(
            ([, ppos]) => ppos === pos
          )?.[0]
          const playerName = (p as PredictionWithPlayer).player?.name ?? t('formula1.unknownPlayer')
          return driver ? { playerName, driver } : null
        })
        .filter((row): row is PlacePredictionRow => row !== null)
    }
    return map
  }, [predictions, t])

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose()
  }

  if (!isOpen) return null

  return (
    <>
      <div
        className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-2 sm:p-3"
        onClick={handleBackdropClick}
      >
        <div
          className="w-[98vw] sm:w-[95vw] md:w-[92vw] lg:w-[90vw] max-w-7xl h-[92vh] max-h-[92vh] overflow-hidden flex flex-col glass-strong rounded-xl sm:rounded-2xl min-h-0"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-4 sm:p-5 border-b border-white/10 bg-gradient-to-b from-primary/10 to-transparent flex items-start justify-between gap-2">
            <div>
              <div className="font-bold text-foreground text-lg sm:text-xl tracking-tight">
                🏎️ {race ? `Race ${race.day}: ${race.city}` : '–'}
              </div>
              <div className="text-sm text-muted-foreground mt-1">
                {race?.raceDate && new Date(race.raceDate).toLocaleDateString()}
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-white/10 text-muted-foreground hover:text-foreground transition-colors shrink-0"
              aria-label={t('stats.close')}
            >
              <span className="text-xl leading-none">×</span>
            </button>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-4 sm:p-5">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-10 h-10 animate-spin text-primary" />
              </div>
            ) : (
              <>
                {predictions.some((p) => p.hiddenUntilLock) && (
                  <p className="text-sm text-muted-foreground italic mb-4">{t('formula1.gridsHidden')}</p>
                )}
                {actualResultsList.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-base font-bold text-foreground mb-3">
                      {t('formula1.actualResults')}
                    </h3>
                    <div className="rounded-xl border border-border/50 bg-card overflow-hidden p-3">
                      <Formula1PredictedGrid actual={race?.actualResults} live={race?.liveResults} />
                    </div>
                  </div>
                )}

                <div className="mb-6">
                  <h3 className="text-base font-bold text-foreground mb-3">
                    {t('formula1.allPlayersTop10')}
                  </h3>
                  <div className="space-y-4">
                    {predictions.map((pred) => {
                      const playerName = (pred as PredictionWithPlayer).player?.name ?? t('formula1.unknownPlayer')
                      return (
                        <div
                          key={pred.id ?? pred._id}
                          className="rounded-xl border border-border/50 bg-card p-4"
                        >
                          <p className="font-semibold text-foreground mb-3">{playerName}</p>
                          <Formula1PredictedGrid
                            predicted={pred.hiddenUntilLock ? null : pred.predictedPositions}
                            actual={race?.actualResults}
                            hidden={Boolean(pred.hiddenUntilLock)}
                            hiddenLabel={t('formula1.gridsHidden')}
                          />
                        </div>
                      )
                    })}
                    {predictions.length === 0 && (
                      <p className="text-muted-foreground text-sm">{t('formula1.noPredictionsForRace')}</p>
                    )}
                  </div>
                </div>

                <h3 className="text-base font-bold text-foreground mb-3">
                  {t('formula1.predictionsByPlace')}
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((pos) => {
                    const suffix = pos === 1 ? 'st' : pos === 2 ? 'nd' : pos === 3 ? 'rd' : 'th'
                    const count = predictionsByPlace[pos]?.length ?? 0
                    return (
                      <button
                        key={pos}
                        type="button"
                        onClick={() => setSelectedPlace(pos)}
                        className="rounded-xl p-3 sm:p-4 border border-border/50 bg-card hover:bg-muted/50 hover:border-primary/30 transition-all text-center"
                      >
                        <span className="block font-bold text-foreground text-base sm:text-lg">
                          {pos}{suffix}
                        </span>
                        <span className="block text-xs text-muted-foreground mt-0.5">
                          {count} {t('formula1.predictions')}
                        </span>
                      </button>
                    )
                  })}
                </div>
              </>
            )}
          </div>

          <div className="p-4 sm:p-5 border-t border-white/10 bg-white/5">
            <button
              type="button"
              onClick={onClose}
              className="w-full py-3 rounded-xl bg-primary text-primary-foreground font-semibold hover:opacity-90 transition-opacity"
            >
              {t('stats.close')}
            </button>
          </div>
        </div>
      </div>

      <Formula1PlacePredictionsModal
        isOpen={selectedPlace !== null}
        position={selectedPlace ?? 1}
        predictions={selectedPlace !== null ? (predictionsByPlace[selectedPlace] ?? []) : []}
        onClose={() => setSelectedPlace(null)}
      />
    </>
  )
}

export default Formula1RacePredictionsModal
